import { SubItem } from '../types/sub-item.types';

export const CompositionShareConfig = {
   min: 0,
   max: 100,
   epsilon: 0.01,
} as const;

export interface ValidationRoot {
   parts: SubItem[];
   parentId?: string;
   id?: number;
   part?: string;
   partDetail?: string;
   materialGrade?: string;
   materialGradeId?: string;
   subItems?: SubItem[];
}

export interface CompositionShareResult {
   total: number;
   details?: Map<string, number>;
}

export const parseShare = (value: string | number | undefined): number => {
   if (typeof value === 'number') return value;
   if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
   }
   return 0;
};

const getActiveParts = (parts: SubItem[]): SubItem[] => {
   return parts.filter((part) => part.active !== false);
};

export const roundShare = (value: number): number => {
   return Math.round(value * 10) / 10;
};

export const formatShare = (value: number): string => {
   if (value % 1 === 0) {
      return value.toString();
   }
   return value.toFixed(1);
};

const sumCompositionShares = (parts: SubItem[]): number => {
   const sum = parts.reduce((total, part) => total + parseShare(part.compositionShare), 0);
   return roundShare(sum);
};

const isPartBeingEdited = (part: SubItem, originalSubItem: Partial<SubItem>): boolean =>
   (originalSubItem.id === undefined || part.id === originalSubItem.id) &&
   (!originalSubItem.parentId || part.parentId === originalSubItem.parentId) &&
   (!originalSubItem.part || part.part === originalSubItem.part) &&
   (!originalSubItem.partDetail || part.partDetail === originalSubItem.partDetail) &&
   (!originalSubItem.materialGrade || part.materialGrade === originalSubItem.materialGrade) &&
   (!originalSubItem.materialGradeId || part.materialGradeId === originalSubItem.materialGradeId);

const getEditedPartsForSingleItem = (activeParts: SubItem[], root: ValidationRoot): SubItem[] =>
   (root.subItems ?? [])
      .map((originalSubItem) => activeParts.find((part) => isPartBeingEdited(part, originalSubItem)))
      .filter((part): part is SubItem => Boolean(part));

export const calculateTotalShare = (
   parts: SubItem[],
   parentId: string,
   currentValue: number = 0,
   root?: ValidationRoot
): CompositionShareResult => {
   if (!parts || parts.length === 0) {
      return { total: currentValue };
   }

   const activeParts = getActiveParts(parts);

   const parentIdMap = new Map<string, SubItem[]>();
   activeParts.forEach((part) => {
      const items = parentIdMap.get(part.parentId) || [];
      items.push(part);
      parentIdMap.set(part.parentId, items);
   });

   const distinctParentIds = Array.from(parentIdMap.keys());
   const isBulkEdit = distinctParentIds.length > 1;

   if (isBulkEdit && parentId === '*') {
      const partsBeingEdited = root?.subItems || [];
      const isEditingExistingParts =
         partsBeingEdited.length > 0 &&
         partsBeingEdited.some((subItem: SubItem) =>
            activeParts.some((part) => part.id === subItem.id && part.part === subItem.part && part.parentId === subItem.parentId)
         );

      const parentTotals = new Map<string, number>();

      if (isEditingExistingParts) {
         const editedPartsByParent = new Map<string, SubItem[]>();
         partsBeingEdited.forEach((part) => {
            const items = editedPartsByParent.get(part.parentId) || [];
            items.push(part);
            editedPartsByParent.set(part.parentId, items);
         });

         editedPartsByParent.forEach((editedPartsForThisParent, pid) => {
            const partsForParent = parentIdMap.get(pid) || [];
            const existingTotal = sumCompositionShares(partsForParent);
            const oldValuesTotal = sumCompositionShares(editedPartsForThisParent);
            const newTotal = existingTotal - oldValuesTotal + currentValue * editedPartsForThisParent.length;
            parentTotals.set(pid, roundShare(newTotal));
         });
      } else {
         parentIdMap.forEach((partsForParent, pid) => {
            const existingTotal = sumCompositionShares(partsForParent);
            const newTotal = existingTotal + currentValue;
            parentTotals.set(pid, roundShare(newTotal));
         });
      }

      const maxTotal = Math.max(...Array.from(parentTotals.values()), 0);
      return { total: maxTotal, details: parentTotals };
   }

   if (isBulkEdit && parentId !== '*') {
      const partsForParent = parentIdMap.get(parentId) || [];
      const existingTotal = sumCompositionShares(partsForParent);
      const total = roundShare(existingTotal + currentValue);
      return { total };
   }

   const isNewPart = root?.id == null && !root?.subItems?.length;

   if (isNewPart) {
      const existingTotal = sumCompositionShares(activeParts);
      const total = roundShare(existingTotal + currentValue);
      return { total };
   }

   const existingTotal = sumCompositionShares(activeParts);
   const editedParts = getEditedPartsForSingleItem(activeParts, root);

   if (editedParts.length === 0) {
      return { total: roundShare(existingTotal) };
   }

   const oldValuesTotal = sumCompositionShares(editedParts);
   const total = existingTotal - oldValuesTotal + currentValue * editedParts.length;
   return { total: roundShare(total) };
};

export const formatCompositionShareError = (total: number, details?: Map<string, number>): string => {
   let errorMessage = `Total composition share must not exceed ${CompositionShareConfig.max}%.`;

   if (details && details.size > 1) {
      const failedParents = Array.from(details.entries())
         .filter(([_, t]) => t > CompositionShareConfig.max + CompositionShareConfig.epsilon)
         .sort(([_, a], [__, b]) => b - a)
         .slice(0, 3)
         .map(([pid, t]) => `${pid}: ${formatShare(t)}%`);

      if (failedParents.length > 0) {
         errorMessage += `\nExceeded for: ${failedParents.join(', ')}`;
         if (details.size > 3) {
            errorMessage += ` (and ${details.size - 3} more)`;
         }
      }
   } else {
      errorMessage += `\nCurrent total: ${formatShare(total)}%.`;
   }

   return errorMessage;
};